import { Skeleton } from "antd";
import {
    useGetOfferCustomField,
    useStoreOfferCustomField,
    useUpdateOfferCustomField,
} from "api/TenantSlug/OfferCustomFields/hooks.ts";
import { AxiosError } from "axios";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect } from "react";
import { toast } from "react-toastify";
import { OfferCustomFieldType } from "types/enums.ts";
import { ApiErrorResponse, FormId } from "types/models.ts";

import { schema, OfferCustomFieldData } from "./formSchema.ts";

import StoreOfferCustomFieldRequest = App.Http.Requests.Offer.StoreOfferCustomFieldRequest;
import UpdateOfferCustomFieldRequest = App.Http.Requests.Offer.UpdateOfferCustomFieldRequest;

interface AddCustomFieldFormContentProps {
    customField?: OfferCustomFieldData
    offerId: string;
    readOnly?: boolean;
}

interface AddCustomRuleFieldFormProps {
    offerId: string;
    customFieldId?: string;
    readOnly?: boolean;
    onSubmit?: () => void;
}

const NEW_CUSTOM_FIELD: StoreOfferCustomFieldRequest = {
    name: '',
    type: OfferCustomFieldType.Rule,
    value: '',
    description: '',
}

const AddCustomRuleFieldFormContent: FC<AddCustomFieldFormContentProps> = ({ customField, readOnly }) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { control, reset } = useGeneralForm();

    useEffect(() => {
        if (customField) {
            reset(customField);
        } else {
            reset({ ...NEW_CUSTOM_FIELD });
        }
    }, [customField]);

    const list = PropertiesList.create(customField ?? NEW_CUSTOM_FIELD, [
        {
            term: tFields("name.label"),
            description: ({ name }) => name,
            render: <InteractiveCell.Input formControl={{ control, name: "name" }} disabled={readOnly} />
        },
        {
            term: tFields("value.label"),
            description: ({ value }) => value,
            render: <InteractiveCell.Input formControl={{ control, name: "value" }} disabled={readOnly} />
        },
        {
            term: tFields("description.label"),
            description: ({ description }) => description,
            render: <InteractiveCell.Input formControl={{ control, name: "description" }} disabled={readOnly} />
        },
    ])

    return <PropertiesList list={list} />
}

export const AddCustomRuleFieldForm: FC<AddCustomRuleFieldFormProps> = ({ offerId, customFieldId, readOnly, onSubmit }) => {
    const { handleErrorsFromErrorResponse } = useGeneralForm<OfferCustomFieldData>();
    const { t } = usePaldockTranslation("forms.add-custom-rule-field")

    const { isFetching, data } = useGetOfferCustomField(offerId, customFieldId!, {}, Boolean(customFieldId));

    const { mutate: createField } = useStoreOfferCustomField(offerId, {});
    const { mutate: updateField } = useUpdateOfferCustomField(offerId, customFieldId!, {});

    const onSuccess = () => {
        toast.success(data?.offerCustomField ? t('messages.update_success') : t('messages.add_success'));
        onSubmit?.();
    }

    const onError = (error: AxiosError<ApiErrorResponse>) => {
        toast.error(data?.offerCustomField ? t('messages.update_error') : t('messages.add_error'));
        handleErrorsFromErrorResponse(error);
    }

    const onSubmitForm = (values: OfferCustomFieldData) => {
        if (!data?.offerCustomField.id) {
            createField(
                values as StoreOfferCustomFieldRequest,
                { onSuccess, onError }
            );
        } else {
            updateField(
                values as UpdateOfferCustomFieldRequest,
                { onSuccess, onError }
            );
        }

    };

    if (isFetching && customFieldId) {
        return <Skeleton.Node rootClassName={'w-full !h-[300px]'} className={'!w-full !h-[300px]'} />;
    }

    return (
        <div className="max-w-xl">
            <GeneralFormProvider<OfferCustomFieldData>
                schema={schema}
                id={FormId.AddCustomRuleFieldForm}
                onSubmit={onSubmitForm}
                resetOnSubmit={false}
            >
                <AddCustomRuleFieldFormContent offerId={offerId} readOnly={readOnly} customField={data?.offerCustomField} />
            </GeneralFormProvider>
        </div>
    )
}
